import { useState } from 'react'
import { useAuth } from '@/context/AuthContext'
import { User, Mail, Shield, Calendar, Building2, Activity, Edit } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@/utils/constants'
import ResetPasswordModal from '@/components/ResetPasswordModal'

const Profile = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)

  const handleEditProfile = () => {
    navigate(ROUTES.EDIT_PROFILE)
  }

  const handleResetPassword = () => {
    setShowResetPasswordModal(true)
  }

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Profile Information</h3>
            <p className="mt-1 text-sm text-gray-600">
              Your account details and preferences
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleResetPassword}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Shield className="h-4 w-4 mr-2" />
              Reset Password
            </button>
            <button
              onClick={handleEditProfile}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Profile
            </button>
          </div>
        </div>

        {/* Basic Information */}
        <div className="p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="flex items-center space-x-3">
              <User className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Full Name</div>
                <div className="text-sm text-gray-600">{user?.first_name || 'Not provided'} {user?.last_name || 'Not provided'}</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Mail className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Email</div>
                <div className="text-sm text-gray-600">{user?.email}</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Shield className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Role</div>
                <div className="text-sm text-gray-600 capitalize">{user?.role}</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Calendar className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Member Since</div>
                <div className="text-sm text-gray-600">
                  {user?.created_on ? new Date(user.created_on).toLocaleDateString() : 'Unknown'}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Activity className="h-5 w-5 text-gray-400" />
              <div>
                <div className="text-sm font-medium text-gray-900">Login Count</div>
                <div className="text-sm text-gray-600">{user?.login_count || 0} times</div>
              </div>
            </div>
            {user?.last_login && (
              <div className="flex items-center space-x-3">
                <Activity className="h-5 w-5 text-gray-400" />
                <div>
                  <div className="text-sm font-medium text-gray-900">Last Login</div>
                  <div className="text-sm text-gray-600">
                    {new Date(user.last_login).toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Brand Access */}
      {user?.brand_access && user.brand_access.length > 0 && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Brand Access</h3>
            <p className="mt-1 text-sm text-gray-600">
              Brands you have access to
            </p>
          </div>
          <div className="p-6">
            {user.role === 'admin' ? (
              <div className="text-center py-4">
                <Shield className="h-8 w-8 mx-auto text-red-500 mb-2" />
                <p className="text-sm text-gray-600">
                  Admin users have access to all brands
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {user.brand_access.map((access) => (
                  <div key={access.brand_id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <Building2 className="h-5 w-5 text-gray-400" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {access.brand_name}
                        </div>
                        {access.brand_description && (
                          <div className="text-xs text-gray-500 mt-1">
                            {access.brand_description}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        access.can_access
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {access.can_access ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => setShowResetPasswordModal(false)}
      />
    </div>
  )
}

export default Profile
